import { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Logo } from '@/components/Logo';
import { CarIcon, Chevron, Drawer, GearIcon, GridIcon, HomeIcon, InfoIcon, LoginIcon, LogoutIcon, PhoneIcon, Spinner, TagIcon, UserIcon, buttonClass } from '@/components/ui/primitives';
import { useLogout, useSession } from '@/features/auth/session';
import { useLocalizedPath } from '@/hooks/useLang';
import { useCategories } from '@/services/catalogue';

function NavItems({ onNavigate, vertical = false }: { onNavigate?: () => void; vertical?: boolean }) {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const items = [
    { to: lp(''), label: t('nav.home'), end: true, icon: <HomeIcon className="h-6 w-6" /> },
    { to: lp('/about'), label: t('nav.about'), icon: <InfoIcon className="h-6 w-6" /> },
    { to: lp('/catalogue'), label: t('nav.catalogue'), icon: <GridIcon className="h-6 w-6" /> },
    { to: lp('/vehicle'), label: vertical ? t('nav.vehicle') : t('nav.vehicleShort'), icon: <CarIcon className="h-6 w-6" /> },
    { to: lp('/brands'), label: t('nav.brands'), icon: <TagIcon className="h-6 w-6" /> },
    { to: lp('/contact'), label: t('nav.contact'), icon: <PhoneIcon className="h-6 w-6" /> },
  ];
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            vertical ? `drawer-item ${isActive ? 'is-active' : ''}` : `nav-link rounded-md px-3 py-2 text-[15px] font-medium transition-colors ${isActive ? 'text-white' : 'text-steel-300 hover:text-white'}`
          }
        >
          {vertical && <span className="drawer-item-icon">{item.icon}</span>}
          {item.label}
        </NavLink>
      ))}
    </>
  );
}

/** "My account" dropdown (signed in) or the dealer-login button (signed out). */
function AccountMenu({ onNavigate, vertical = false }: { onNavigate?: () => void; vertical?: boolean }) {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const { data: user } = useSession();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !wrapRef.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) {
    return vertical ? (
      <NavLink to={lp('/login')} onClick={onNavigate} className="drawer-item">
        <span className="drawer-item-icon">
          <LoginIcon className="h-6 w-6 rtl:-scale-x-100" />
        </span>
        {t('nav.dealerLogin')}
      </NavLink>
    ) : (
      <NavLink to={lp('/login')} onClick={onNavigate} className={buttonClass('secondary', 'md', '')}>
        {t('nav.dealerLogin')}
      </NavLink>
    );
  }
  const links = [
    { to: lp('/account'), label: t('nav.account'), icon: <UserIcon className="h-6 w-6" /> },
    ...(user.role === 'ADMIN' ? [{ to: lp('/admin'), label: t('nav.admin'), icon: <GearIcon className="h-6 w-6" /> }] : []),
  ];
  if (vertical) {
    return (
      <div className="flex flex-col gap-1">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} onClick={onNavigate} className={({ isActive }) => `drawer-item ${isActive ? 'is-active' : ''}`}>
            <span className="drawer-item-icon">{l.icon}</span>
            {l.label}
          </NavLink>
        ))}
        <button type="button" onClick={() => logout.mutate()} className="drawer-item">
          <span className="drawer-item-icon">
            <LogoutIcon className="h-6 w-6 rtl:-scale-x-100" />
          </span>
          {t('nav.logout')}
        </button>
      </div>
    );
  }
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-10 items-center gap-2 rounded-xl border border-ink-500 bg-ink-800/80 px-3 text-sm font-semibold text-steel-100 transition-colors hover:border-volt-500/60 hover:text-white"
      >
        <UserIcon className="h-5 w-5 text-volt-300" />
        <span>{t('nav.account')}</span>
        <Chevron dir="down" className={`h-4 w-4 text-steel-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" className="animate-fade-in absolute end-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-ink-600 bg-ink-850 p-1.5 shadow-2xl">
          <div className="border-b border-ink-700 px-3 py-2">
            <p className="truncate text-sm font-semibold text-steel-100">{user.name}</p>
            <p className="truncate text-xs text-steel-500" dir="ltr">
              {user.email}
            </p>
          </div>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} role="menuitem" onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2 text-sm font-medium text-steel-200 hover:bg-ink-700 hover:text-white">
              {l.label}
            </NavLink>
          ))}
          <button type="button" role="menuitem" onClick={() => logout.mutate()} className="block w-full rounded-lg px-3 py-2 text-start text-sm font-medium text-steel-200 hover:bg-ink-700 hover:text-white">
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

export function SiteLayout() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = /^\/(en|ar|he)\/?$/.test(location.pathname);

  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0 });
  }, [location.pathname, location.hash]);

  return (
    <div className="flex min-h-dvh flex-col pb-24 lg:pb-0">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-volt-500 focus:px-4 focus:py-2 focus:text-white">
        {t('common.skipToContent')}
      </a>
      {/* Floating technical header */}
      <header className="fixed inset-x-0 top-0 z-40 px-3 pt-3 sm:px-6">
        <div className="nav-shell mx-auto flex h-16 max-w-7xl items-center gap-3 rounded-2xl border-volt-500/25 px-3.5 sm:h-[4.5rem] sm:rounded-[1.15rem] sm:px-5">
          <NavLink to={lp('')} aria-label="PRO PARTS" className="shrink-0 scale-[0.92] origin-left sm:scale-100 rtl:origin-right">
            <Logo />
          </NavLink>
          <nav aria-label={t('nav.mainNavigation')} className="hidden flex-1 items-center justify-center gap-1 lg:flex">
            <NavItems />
          </nav>
          <div className="ms-auto hidden items-center gap-3 lg:flex">
            <LanguageSwitcher compact />
            <AccountMenu />
          </div>
          <button type="button" className="ms-auto rounded-xl border border-volt-500/25 bg-ink-900/75 p-2.5 text-steel-100 shadow-[inset_0_1px_rgb(255_255_255/0.05)] transition-colors hover:border-volt-500/60 hover:bg-ink-800 lg:hidden" onClick={() => setMenuOpen(true)} aria-label={t('common.menu')} aria-expanded={menuOpen}>
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </header>

      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} title={t('common.menu')} side="end" artwork>
        <nav aria-label={t('nav.mainNavigation')} className="flex flex-col gap-1">
          <NavItems vertical onNavigate={() => setMenuOpen(false)} />
        </nav>
        <div className="mt-4 border-t border-white/10 pt-4">
          <AccountMenu vertical onNavigate={() => setMenuOpen(false)} />
        </div>
        <div className="mt-5">
          <LanguageSwitcher />
        </div>
      </Drawer>

      <main id="main" className={`flex-1 ${isHome ? '' : 'pt-[5rem] sm:pt-[5.5rem]'}`} tabIndex={-1}>
        <Suspense fallback={<PageSpinner />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <MobileDock />
    </div>
  );
}

function MobileDock() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const items = [
    {
      to: lp(''),
      label: t('nav.home'),
      end: true,
      icon: (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <path d="m3.5 11 8.5-7 8.5 7v9h-6v-5h-5v5h-6z" strokeLinejoin="round" />
        </svg>
      ),
    },
    { to: lp('/catalogue'), label: t('nav.catalogue'), icon: <GridIcon className="h-6 w-6" /> },
    { to: lp('/vehicle'), label: t('nav.vehicleShort'), icon: <CarIcon className="h-6 w-6" /> },
    { to: lp('/account'), label: t('nav.account'), icon: <UserIcon className="h-6 w-6" /> },
  ];
  return (
    <nav aria-label={t('nav.mainNavigation')} className="fixed inset-x-3 bottom-3 z-40 lg:hidden">
      {/* Fade behind the floating bar: page content scrolling underneath never shows in the gap. */}
      <div aria-hidden className="pointer-events-none absolute inset-x-[-0.75rem] -bottom-3 h-[6.5rem] bg-gradient-to-t from-ink-950 via-ink-950/90 to-transparent" />
      <div className="finder-shell relative mx-auto grid max-w-md grid-cols-4 gap-1 rounded-2xl p-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))]">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                'relative flex min-h-[3.6rem] min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-1.5 text-[11px] font-bold transition-all',
                isActive
                  ? 'border border-volt-400/40 bg-gradient-to-b from-volt-500/45 to-volt-700/35 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_0_22px_-6px_rgb(46_139_255/0.8)] after:absolute after:inset-x-3 after:-bottom-[0.45rem] after:h-0.5 after:rounded-full after:bg-volt-400 after:shadow-[0_0_12px_rgb(46_139_255/0.95)]'
                  : 'text-steel-200 hover:bg-white/5 hover:text-white',
              ].join(' ')
            }
          >
            {item.icon}
            <span className="w-full truncate text-center">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function PageSpinner() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center text-volt-400" role="status">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

function Footer() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const { data: categories } = useCategories();
  const year = new Date().getFullYear();
  return (
    <footer className="ambient-grid section-rule relative overflow-hidden border-t border-ink-700 bg-ink-900">
      <div aria-hidden className="hairline-x absolute inset-x-0 top-0" />
      <div aria-hidden className="keep-tracking pointer-events-none absolute bottom-0 start-0 font-[Barlow_Condensed] text-[22vw] leading-none font-bold tracking-[0.05em] text-white/[0.025] uppercase select-none" dir="ltr">
        PRO PARTS
      </div>
      <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-10 px-5 py-14 sm:px-6 md:grid-cols-4 lg:py-20">
        <div className="col-span-2 space-y-5 md:col-span-1 md:pe-8">
          <Logo className="scale-110 origin-left rtl:origin-right" />
          <p className="max-w-xs border-s-2 border-volt-500 ps-4 text-sm leading-relaxed text-steel-400">{t('common.tagline')}</p>
        </div>
        <FooterColumn title={t('footer.catalogue')}>
          <FooterLink to={lp('/catalogue')}>{t('nav.catalogue')}</FooterLink>
          {(categories?.items ?? []).map((c) => (
            <FooterLink key={c.slug} to={lp(`/catalogue?category=${c.slug}`)}>
              {c.name}
            </FooterLink>
          ))}
          <FooterLink to={lp('/vehicle')}>{t('nav.vehicle')}</FooterLink>
        </FooterColumn>
        <FooterColumn title={t('footer.company')}>
          <FooterLink to={lp('/about')}>{t('nav.about')}</FooterLink>
          <FooterLink to={lp('/brands')}>{t('nav.brands')}</FooterLink>
          <FooterLink to={lp('/contact')}>{t('nav.contact')}</FooterLink>
        </FooterColumn>
        <FooterColumn title={t('footer.business')}>
          <FooterLink to={lp('/login')}>{t('nav.dealerLogin')}</FooterLink>
          <FooterLink to={lp('/contact')}>{t('home.requestAccount')}</FooterLink>
        </FooterColumn>
      </div>
      <div className="relative border-t border-ink-800 py-6 text-center text-xs text-steel-500">{t('footer.rights', { year })}</div>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-4 text-xs font-bold tracking-[0.18em] text-steel-400 uppercase">{title}</h2>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <NavLink to={to} className="text-sm text-steel-300 transition-colors hover:text-white">
        {children}
      </NavLink>
    </li>
  );
}
